/**
 * What a vendor's `kyc_status` means, in words a vendor can act on.
 *
 * This used to live inside `dashboard-page.tsx`, which is why it was only ever visible on
 * one screen. Two places needed it and could not reach it:
 *
 *   - The screen shown straight after registration, which instead printed the raw profile
 *     id and the raw enum ("Vendor profile cmt6… → DOCUMENTS_SUBMITTED") — plumbing, not an
 *     answer to "am I approved?".
 *   - Every screen after login, where a REJECTED or SUSPENDED vendor could work all day on
 *     listings that no customer can see, with nothing saying why.
 *
 * `blocks_trading` is the field that matters most: it separates "we are still looking at
 * your application" from "you cannot trade". Those need different urgency, and lumping
 * PENDING in with REJECTED under one warning tone is how a rejection goes unnoticed.
 */
import type { StatusTone } from '@/components/ui/status-pill';

export type KycPresentation = {
  tone: StatusTone;
  /** Short label for a pill. */
  short: string;
  /** One line, stating the state. */
  headline: string;
  /** One line, stating what happens next or what the vendor should do. */
  detail: string;
  /**
   * True when nothing the vendor lists can reach a customer.
   *
   * Drives whether the status is shown as a persistent banner across the workspace rather
   * than a card on the dashboard: if your listings are invisible, that fact belongs on
   * every page, not on the one page you might not open.
   */
  blocks_trading: boolean;
};

export const KYC_PRESENTATION: Record<string, KycPresentation> = {
  PENDING: {
    tone: 'warning',
    short: 'Pending',
    headline: 'Application incomplete',
    detail: 'Finish onboarding to send your profile for review.',
    blocks_trading: true,
  },
  DOCUMENTS_SUBMITTED: {
    tone: 'info',
    short: 'In review',
    headline: 'Submitted for review',
    detail:
      'Your workspace is fully open — set up services, slots and listings now. Customers can book you once an admin approves your documents.',
    // Not a failure, but customers still cannot book you — say so rather than imply you are live.
    blocks_trading: true,
  },
  VERIFIED: {
    tone: 'success',
    short: 'Approved',
    headline: 'Approved',
    detail: 'Your profile is live and discoverable by customers.',
    blocks_trading: false,
  },
  REJECTED: {
    tone: 'destructive',
    short: 'Rejected',
    headline: 'Application rejected',
    detail: 'Please contact support to find out what needs fixing.',
    blocks_trading: true,
  },
  SUSPENDED: {
    tone: 'destructive',
    short: 'Suspended',
    headline: 'Account suspended',
    detail: 'Your listings are hidden from customers. Contact support.',
    blocks_trading: true,
  },
};

/**
 * Never returns undefined.
 *
 * A status the console has not been taught about must not render as a blank space where
 * an approval decision should be — that reads as "approved" to anyone skimming.
 */
export function kyc_presentation(status: string | null | undefined): KycPresentation {
  if (status && KYC_PRESENTATION[status]) return KYC_PRESENTATION[status];
  return {
    tone: 'warning',
    short: status ?? 'Unknown',
    headline: 'Status unavailable',
    detail: 'We could not read your account status. Contact support if this persists.',
    blocks_trading: true,
  };
}
